import React, { useState, useEffect, useCallback } from 'react';
import { Select, Button, Modal, Input, Form, message, Space, InputNumber, Divider, Collapse, Card, Tag, Row, Col, Switch, Alert } from 'antd';
import { PlusOutlined, FolderOutlined, SettingOutlined, ExperimentOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext.jsx';
import apiService from '../services/apiService';
import { ConfigurationListView, RulingConfigurationManager } from './ruling';
import { RulingConfigUtils } from '../utils/rulingConfigUtils';

const { Option } = Select;
const { TextArea } = Input;
const { Panel } = Collapse;

// Utility functions for handling hybrid scoring system
const ScoringUtils = {
  // Check if a scorecard uses match-based scoring
  isMatchBased: (scorecardNum) => {
    return scorecardNum === 3 || scorecardNum === 4;
  },
  
  // Get scorecard type
  getScorecardType: (scorecardNum) => {
    return ScoringUtils.isMatchBased(scorecardNum) ? 'match-based' : 'numeric';
  },
  
  // Validate threshold configuration
  validateThresholds: (scorecard, values) => {
    const scorecardNum = parseInt(scorecard.split('_')[1]);
    
    if (ScoringUtils.isMatchBased(scorecardNum)) {
      // Validate match-based scoring
      if (!values.pass_set || !Array.isArray(values.pass_set) || values.pass_set.length === 0) {
        return 'Pass set is required and must contain at least one value';
      }
      if (!values.fail_set || !Array.isArray(values.fail_set) || values.fail_set.length === 0) {
        return 'Fail set is required and must contain at least one value';
      }
      return null;
    } else {
      // Validate numeric scoring
      if (values.refer_threshold > values.pass_threshold) {
        return 'Refer threshold cannot be greater than pass threshold';
      }
      if (values.pass_threshold < 0 || values.pass_threshold > 10) {
        return 'Pass threshold must be between 0 and 10';
      }
      if (values.refer_threshold < 0 || values.refer_threshold > 10) {
        return 'Refer threshold must be between 0 and 10';
      }
      return null;
    }
  },

  // Get default values for a scorecard
  getDefaultValues: (scorecardNum) => {
    if (ScoringUtils.isMatchBased(scorecardNum)) {
      return {
        pass_set: ['match'],
        refer_set: [],
        fail_set: ['no match']
      };
    } else {
      return {
        pass_threshold: 8,
        refer_threshold: 6
      };
    }
  }
};

// Match-based threshold configuration component
const MatchBasedThresholdConfig = ({ scorecard, form }) => {
  const [inputValue, setInputValue] = useState('');
  const [inputVisible, setInputVisible] = useState(false);
  const [editingSet, setEditingSet] = useState(null);

  const handleAddValue = (setType) => {
    if (inputValue && inputValue.trim()) {
      const currentValues = form.getFieldValue(`${scorecard}_${setType}`) || [];
      if (!currentValues.includes(inputValue.trim())) {
        form.setFieldsValue({
          [`${scorecard}_${setType}`]: [...currentValues, inputValue.trim()]
        });
      }
    }
    setInputValue('');
    setInputVisible(false);
    setEditingSet(null);
  };

  const handleRemoveValue = (setType, valueToRemove) => {
    const currentValues = form.getFieldValue(`${scorecard}_${setType}`) || [];
    form.setFieldsValue({
      [`${scorecard}_${setType}`]: currentValues.filter(v => v !== valueToRemove)
    });
  };

  const renderValueSet = (setType, label, color) => {
    const values = form.getFieldValue(`${scorecard}_${setType}`) || [];
    const isEditing = editingSet === setType;

    return (
      <div style={{ marginBottom: 16 }}>
        <div style={{ 
          marginBottom: 8, 
          fontWeight: 500, 
          color: 'var(--text-primary)',
          fontSize: '13px'
        }}>
          {label}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
          {values.map(value => (
            <Tag
              key={value}
              color={color}
              closable
              onClose={() => handleRemoveValue(setType, value)}
              style={{ marginBottom: 4 }}
            >
              {value}
            </Tag>
          ))}
          
          {isEditing ? (
            <Input
              size="small"
              style={{ width: 120 }}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onPressEnter={() => handleAddValue(setType)}
              onBlur={() => handleAddValue(setType)}
              autoFocus
            />
          ) : (
            <Tag
              onClick={() => {
                setEditingSet(setType);
                setInputVisible(true);
                setInputValue('');
              }}
              style={{
                background: 'var(--bg-tertiary)',
                borderStyle: 'dashed',
                cursor: 'pointer'
              }}
            >
              <PlusOutlined /> Add Value
            </Tag>
          )}
        </div>
        
        <Form.Item
          name={`${scorecard}_${setType}`}
          hidden
          initialValue={setType === 'pass_set' ? ['match'] : setType === 'fail_set' ? ['no match'] : []}
        >
          <Input />
        </Form.Item>
      </div>
    );
  };

  return (
    <div>
      <div style={{ 
        fontSize: '12px', 
        color: 'var(--text-secondary)', 
        marginBottom: 12,
        lineHeight: '1.4'
      }}>
        Match-based scoring: Values are compared as exact strings (case-insensitive)
      </div>
      
      {renderValueSet('pass_set', 'Pass Values', 'green')}
      {renderValueSet('refer_set', 'Refer Values', 'orange')}
      {renderValueSet('fail_set', 'Fail Values', 'red')}
    </div>
  );
};

// Numeric threshold configuration component
const NumericThresholdConfig = ({ scorecard }) => {
  return (
    <div>
      <div style={{ 
        fontSize: '12px', 
        color: 'var(--text-secondary)', 
        marginBottom: 12,
        lineHeight: '1.4'
      }}>
        Numeric scoring (0-10 scale): Score ≥ pass = Pass, refer ≤ score &lt; pass = Refer, score &lt; refer = Fail
      </div>
      
      <Form.Item
        name={`${scorecard}_pass`}
        label="Pass Threshold (≥)"
        rules={[
          { required: true, message: 'Required' },
          { type: 'number', min: 0, max: 10, message: '0-10' }
        ]}
        style={{ marginBottom: 12 }}
      >
        <InputNumber
          min={0}
          max={10}
          step={0.1}
          style={{ width: '100%' }}
          size="small"
          placeholder="8.0"
        />
      </Form.Item>
      
      <Form.Item
        name={`${scorecard}_refer`}
        label="Refer Threshold (≥)"
        rules={[
          { required: true, message: 'Required' },
          { type: 'number', min: 0, max: 10, message: '0-10' },
          ({ getFieldValue }) => ({
            validator(_, value) {
              const passValue = getFieldValue(`${scorecard}_pass`);
              if (!value || !passValue || value <= passValue) {
                return Promise.resolve();
              }
              return Promise.reject(new Error('Must be ≤ Pass'));
            },
          })
        ]}
        style={{ marginBottom: 0 }}
      >
        <InputNumber
          min={0}
          max={10}
          step={0.1}
          style={{ width: '100%' }}
          size="small"
          placeholder="6.0"
        />
      </Form.Item>
    </div>
  );
};

const ProjectDropdown = ({ currentProject, onProjectChange, style, canCreateProject = true }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [thresholdDefaults, setThresholdDefaults] = useState(null);
  const [userThresholds, setUserThresholds] = useState(null);
  const [useRulingSystem, setUseRulingSystem] = useState(false);
  const [selectedRulingConfig, setSelectedRulingConfig] = useState(null);
  const [isRulingConfigModalVisible, setIsRulingConfigModalVisible] = useState(false);
  const [form] = Form.useForm();
  const { getAuthToken } = useAuth();

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      const response = await apiService.getProjects(token);
      setProjects(response.projects || []);
      
      // If no current project is selected and we have projects, select the first one
      if (!currentProject && response.projects && response.projects.length > 0) {
        onProjectChange(response.projects[0]);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
      message.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [currentProject, onProjectChange, getAuthToken]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const loadThresholdDefaults = async () => {
    try {
      const token = getAuthToken();
      
      // Try to load user's existing threshold settings first
      try {
        const userResponse = await apiService.getThresholdSettings(token);
        if (!userResponse.is_default) {
          setUserThresholds(userResponse.data);
          setThresholdDefaults(userResponse.data);
        } else {
          // Use system defaults
          const defaultsResponse = await apiService.getThresholdDefaults(token);
          setThresholdDefaults(defaultsResponse.data);
        }
      } catch (error) {
        // User doesn't have custom thresholds, load system defaults
        console.log('User has no custom thresholds, loading system defaults');
        const defaultsResponse = await apiService.getThresholdDefaults(token);
        setThresholdDefaults(defaultsResponse.data);
      }
    } catch (error) {
      console.error('Failed to load threshold settings:', error);
      // Set hybrid fallback defaults
      const hybridDefaults = {};
      for (let i = 1; i <= 4; i++) {
        hybridDefaults[`scorecard_${i}`] = ScoringUtils.getDefaultValues(i);
      }
      setThresholdDefaults(hybridDefaults);
    }
  };

  const handleCreateProject = async () => {
    await loadThresholdDefaults();
    
    // Set initial threshold values in form with hybrid system
    const initialThresholds = userThresholds || thresholdDefaults;
    if (initialThresholds) {
      const formValues = {};
      
      for (let i = 1; i <= 4; i++) {
        const scorecard = `scorecard_${i}`;
        const scorecardData = initialThresholds[scorecard];
        
        if (scorecardData) {
          if (ScoringUtils.isMatchBased(i)) {
            // Match-based scoring (scorecards 3 & 4)
            formValues[`${scorecard}_pass_set`] = scorecardData.pass_set || ['match'];
            formValues[`${scorecard}_refer_set`] = scorecardData.refer_set || [];
            formValues[`${scorecard}_fail_set`] = scorecardData.fail_set || ['no match'];
          } else {
            // Numeric scoring (scorecards 1 & 2)
            formValues[`${scorecard}_pass`] = scorecardData.pass_threshold || 8;
            formValues[`${scorecard}_refer`] = scorecardData.refer_threshold || 6;
          }
        } else {
          // Use defaults if no data
          const defaults = ScoringUtils.getDefaultValues(i);
          if (ScoringUtils.isMatchBased(i)) {
            formValues[`${scorecard}_pass_set`] = defaults.pass_set;
            formValues[`${scorecard}_refer_set`] = defaults.refer_set;
            formValues[`${scorecard}_fail_set`] = defaults.fail_set;
          } else {
            formValues[`${scorecard}_pass`] = defaults.pass_threshold;
            formValues[`${scorecard}_refer`] = defaults.refer_threshold;
          }
        }
      }
      
      form.setFieldsValue(formValues);
    }
    
    setIsCreateModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsCreateModalVisible(false);
    setUseRulingSystem(false);
    setSelectedRulingConfig(null);
    setIsRulingConfigModalVisible(false);
    form.resetFields();
    setThresholdDefaults(null);
    setUserThresholds(null);
  };

  const handleCreateSubmit = async (values) => {
    try {
      const token = getAuthToken();
      
      // Create project with basic info
      const projectData = {
        name: values.name,
        description: values.description
      };
      
      const newProject = await apiService.createProject(projectData, token);
      
      if (useRulingSystem) {
        // Use the new ruling configuration system
        if (selectedRulingConfig) {
          try {
            // If a ruling configuration was selected, activate it for this project
            const projectConfig = {
              ...selectedRulingConfig,
              project_id: newProject.id,
              is_active: true
            };
            
            // Remove the ID to create a new configuration for this project
            delete projectConfig._id;
            delete projectConfig.id;
            
            await apiService.createRulingConfiguration(projectConfig, token, newProject.id);
            message.success('Project created with ruling configuration!');
          } catch (error) {
            console.warn('Failed to set ruling configuration, using defaults:', error);
            message.warning('Project created successfully, but ruling configuration could not be saved');
          }
        }
        // If no configuration selected, project will use default/user global configuration
      } else {
        // Use the legacy threshold system
        const thresholdData = {};
        let hasValidationErrors = false;
        
        for (let i = 1; i <= 4; i++) {
          const scorecard = `scorecard_${i}`;
          
          if (ScoringUtils.isMatchBased(i)) {
            // Match-based scoring (scorecards 3 & 4)
            thresholdData[scorecard] = {
              pass_set: values[`${scorecard}_pass_set`] || ['match'],
              refer_set: values[`${scorecard}_refer_set`] || [],
              fail_set: values[`${scorecard}_fail_set`] || ['no match']
            };
            
            // Validate match-based thresholds
            const validation = ScoringUtils.validateThresholds(scorecard, thresholdData[scorecard]);
            if (validation) {
              message.error(`${scorecard}: ${validation}`);
              hasValidationErrors = true;
            }
          } else {
            // Numeric scoring (scorecards 1 & 2)
            thresholdData[scorecard] = {
              pass_threshold: values[`${scorecard}_pass`],
              refer_threshold: values[`${scorecard}_refer`]
            };
            
            // Validate numeric thresholds
            const validation = ScoringUtils.validateThresholds(scorecard, thresholdData[scorecard]);
            if (validation) {
              message.error(`${scorecard}: ${validation}`);
              hasValidationErrors = true;
            }
          }
        }
        
        if (hasValidationErrors) {
          return;
        }
        
        // Check if thresholds are different from defaults/user settings
        const baselineThresholds = userThresholds || thresholdDefaults;
        let isCustomThresholds = false;
        
        for (let i = 1; i <= 4; i++) {
          const scorecard = `scorecard_${i}`;
          const currentThreshold = thresholdData[scorecard];
          const baselineThreshold = baselineThresholds?.[scorecard];
          
          if (ScoringUtils.isMatchBased(i)) {
            if (JSON.stringify(currentThreshold.pass_set) !== JSON.stringify(baselineThreshold?.pass_set) ||
                JSON.stringify(currentThreshold.refer_set) !== JSON.stringify(baselineThreshold?.refer_set) ||
                JSON.stringify(currentThreshold.fail_set) !== JSON.stringify(baselineThreshold?.fail_set)) {
              isCustomThresholds = true;
              break;
            }
          } else {
            if (currentThreshold.pass_threshold !== baselineThreshold?.pass_threshold ||
                currentThreshold.refer_threshold !== baselineThreshold?.refer_threshold) {
              isCustomThresholds = true;
              break;
            }
          }
        }
        
        // Only set project-specific thresholds if they're different from the baseline
        if (isCustomThresholds) {
          try {
            await apiService.updateProjectThresholds(newProject.id, thresholdData, token);
          } catch (error) {
            console.warn('Failed to set project thresholds, using defaults:', error);
            message.warning('Project created successfully, but threshold settings could not be saved');
          }
        }
      }
      
      // Add the new project to the list
      const updatedProjects = [...projects, newProject];
      setProjects(updatedProjects);
      
      // Select the new project
      onProjectChange(newProject);
      
      // Reset form and modal state
      setIsCreateModalVisible(false);
      setUseRulingSystem(false);
      setSelectedRulingConfig(null);
      form.resetFields();
      message.success('Project created successfully!');
    } catch (error) {
      console.error('Failed to create project:', error);
      message.error('Failed to create project');
    }
  };

  const handleProjectSelect = (projectId) => {
    const selectedProject = projects.find(p => p.id === projectId);
    if (selectedProject) {
      onProjectChange(selectedProject);
    }
  };

  const formatProjectOption = (project) => {
    const isShared = project.is_shared || !project.is_owner;
    const permissionColor = {
      'owner': '#52c41a',
      'manage': '#8b5cf6',
      'action': '#1890ff',
      'read': '#faad14'
    };
    
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>
          <FolderOutlined style={{ marginRight: 8, color: isShared ? '#8b5cf6' : '#1890ff' }} />
          {project.name}
          {isShared && (
            <Tag 
              color={permissionColor[project.permission_level] || 'default'}
              style={{ marginLeft: 8, fontSize: '10px' }}
            >
              {project.permission_level}
            </Tag>
          )}
        </span>
        <span style={{ fontSize: '12px', color: '#666' }}>
          {project.chat_count || 0} chats
        </span>
      </div>
    );
  };

  return (
    <>
      <div style={{ ...style }}>
        <Select
          style={{ 
            width: '100%'
          }}
          className="project-dropdown"
          placeholder="Select a project"
          value={currentProject?.id}
          onChange={handleProjectSelect}
          loading={loading}
          size="large"
          popupRender={(menu) => (
            <>
              {menu}
              <div style={{ padding: 8, borderTop: '1px solid var(--border-primary)' }}>
                <Button
                  type="dashed"
                  onClick={handleCreateProject}
                  block
                  disabled={!canCreateProject}
                  icon={<PlusOutlined />}
                  title={canCreateProject ? "Create a new project" : "License required to create projects"}
                  style={{ 
                    borderColor: 'var(--border-secondary)',
                    color: 'var(--text-secondary)',
                    backgroundColor: 'transparent'
                  }}
                >
                  Create New Project
                </Button>
              </div>
            </>
          )}
        >
          {projects.map(project => (
            <Option key={project.id} value={project.id}>
              {formatProjectOption(project)}
            </Option>
          ))}
        </Select>
      </div>

      <Modal
        title="Create New Project"
        open={isCreateModalVisible}
        onCancel={handleModalCancel}
        onOk={() => form.submit()}
        confirmLoading={loading}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateSubmit}
        >
          <Form.Item
            name="name"
            label="Project Name"
            rules={[
              { required: true, message: 'Please enter a project name' },
              { min: 3, message: 'Project name must be at least 3 characters' },
              { max: 50, message: 'Project name cannot exceed 50 characters' }
            ]}
          >
            <Input placeholder="Enter project name" />
          </Form.Item>
          
          <Form.Item
            name="description"
            label="Description (Optional)"
            rules={[
              { max: 500, message: 'Description cannot exceed 500 characters' }
            ]}
          >
            <TextArea
              rows={3}
              placeholder="Enter project description (optional)"
            />
          </Form.Item>

          <Divider />
          
          <Collapse 
            defaultActiveKey={['evaluation']}
            items={[
              {
                key: 'evaluation',
                label: (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <SettingOutlined style={{ color: '#8b5cf6' }} />
                    <span style={{ fontWeight: 500 }}>App Evaluation Configuration</span>
                  </div>
                ),
                children: (
                  <div style={{ padding: '8px 0' }}>
                    {/* Configuration System Toggle */}
                    <div style={{ marginBottom: 20 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                        <Switch
                          checked={useRulingSystem}
                          onChange={setUseRulingSystem}
                        />
                        <span style={{ fontWeight: 500 }}>
                          {useRulingSystem ? 'Advanced Ruling Configuration System' : 'Legacy Threshold System'}
                        </span>
                      </div>
                      
                      <Alert
                        message={useRulingSystem ? 
                          "Advanced System: Create flexible business rules with complex conditions and logic operators" :
                          "Legacy System: Simple threshold-based evaluation for compatibility"
                        }
                        type={useRulingSystem ? "info" : "warning"}
                        size="small"
                        showIcon
                        style={{ fontSize: '12px' }}
                      />
                    </div>

                    {/* Ruling Configuration System */}
                    {useRulingSystem ? (
                      <div>
                        <div style={{ marginBottom: 16 }}>
                          <Button
                            type="dashed"
                            icon={<ExperimentOutlined />}
                            onClick={() => setIsRulingConfigModalVisible(true)}
                            style={{ width: '100%' }}
                          >
                            Configure Ruling System
                          </Button>
                        </div>
                        
                        {selectedRulingConfig && (
                          <Card size="small" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                            <div style={{ fontSize: '13px' }}>
                              <div><strong>Selected Configuration:</strong> {selectedRulingConfig.name}</div>
                              {selectedRulingConfig.description && (
                                <div style={{ color: 'var(--text-secondary)' }}>{selectedRulingConfig.description}</div>
                              )}
                              <div style={{ marginTop: 8 }}>
                                <Tag size="small">Version {selectedRulingConfig.version}</Tag>
                                {selectedRulingConfig.is_active && <Tag color="green" size="small">Active</Tag>}
                              </div>
                            </div>
                          </Card>
                        )}
                      </div>
                    ) : (
                      /* Legacy Threshold System */
                      <div>
                        <div style={{ 
                          color: 'var(--text-secondary)', 
                          fontSize: '13px', 
                          marginBottom: 16,
                          lineHeight: '1.4'
                        }}>
                          <strong>Hybrid Scoring System:</strong><br />
                          • <strong>Scorecards 1 & 2:</strong> Numeric scoring (0-10 scale)<br />
                          • <strong>Scorecards 3 & 4:</strong> Match-based scoring (string matching)<br />
                          {userThresholds && <span style={{ color: '#8b5cf6' }}> (Using your global settings)</span>}
                          {!userThresholds && <span style={{ color: '#9ca3af' }}> (Using system defaults)</span>}
                        </div>
                    
                        <Row gutter={16}>
                          {[1, 2, 3, 4].map(scorecard => {
                            const isMatchBased = ScoringUtils.isMatchBased(scorecard);
                            const scoringType = ScoringUtils.getScorecardType(scorecard);
                            
                            return (
                              <Col span={12} key={scorecard}>
                                <Card 
                                  size="small" 
                                  title={
                                    <div>
                                      <span>Scorecard {scorecard}</span>
                                      <Tag 
                                        size="small" 
                                        color={isMatchBased ? 'purple' : 'blue'}
                                        style={{ marginLeft: 8, fontSize: '10px' }}
                                      >
                                        {scoringType}
                                      </Tag>
                                    </div>
                                  }
                                  style={{ 
                                    backgroundColor: 'var(--bg-tertiary)',
                                    border: '1px solid var(--border-secondary)',
                                    marginBottom: 16
                                  }}
                                  headStyle={{
                                    backgroundColor: 'var(--bg-secondary)',
                                    color: 'var(--text-primary)',
                                    fontSize: '14px',
                                    minHeight: 'auto',
                                    padding: '8px 12px'
                                  }}
                                  bodyStyle={{ padding: '12px' }}
                                >
                                  {isMatchBased ? (
                                    <MatchBasedThresholdConfig 
                                      scorecard={`scorecard_${scorecard}`} 
                                      form={form}
                                    />
                                  ) : (
                                    <NumericThresholdConfig 
                                      scorecard={`scorecard_${scorecard}`}
                                    />
                                  )}
                                </Card>
                              </Col>
                            );
                          })}
                        </Row>
                      </div>
                    )}
                  </div>
                )
              }
            ]}
          />
        </Form>
      </Modal>

      {/* Ruling Configuration Modal */}
      <Modal
        title="Ruling Configuration"
        open={isRulingConfigModalVisible}
        onCancel={() => setIsRulingConfigModalVisible(false)}
        width="90%"
        style={{ top: 20 }}
        footer={null}
      >
        <ConfigurationListView
          projectId={null}
          selectionMode={true}
          onSelect={(config) => {
            setSelectedRulingConfig(config);
            setIsRulingConfigModalVisible(false);
          }}
        />
      </Modal>
    </>
  );
};

export default ProjectDropdown;